<template>
  <div class="content-wrapper view-user-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <h4><i class="fas fa-users"></i> จัดการผู้ใช้</h4>
          <!-- add user -->
          <b-card no-body>
            <b-tabs card>
              <b-tab title="ผู้ใช้งานทั้งหมด" active v-if="isManager">
                <b-button
                  class="mb-2"
                  variant="primary"
                  data-testid="add-user-button"
                  @click="openNewUserModal"
                >
                  <i class="fa fa-user-plus" aria-hidden="true"></i>
                  เพิ่มผู้ใช้งาน
                </b-button>

                <!-- table -->
                <div data-testid="branch-members-table">
                  <b-table
                    :style="{ minHeight: '300px' }"
                    outlined
                    noCollapse
                    sort-icon-left
                    sort-by="creationDt"
                    :sort-desc="false"
                    head-variant="light"
                    :items="branchMembers"
                    :fields="fields"
                    hover
                    responsive
                  >
                    <template #cell(index)="data"
                      >{{ data.index + 1 }}.</template
                    >
                    <template #cell(fullName)="data">
                      {{ data.value }}
                      <span
                        v-if="
                          data.item.user &&
                          data.item.user.email == userInfo.email
                        "
                        class="bg-secondary rounded p-1 mx-1"
                        >คุณ</span
                      >
                    </template>
                    <template #cell(doctorCode)="data">
                      {{ data.item.doctorCode || "-" }}
                    </template>
                    <template v-slot:cell(authId)="data">
                      <b-row>
                        <b-col>
                          <multiselect
                            v-model="data.item.multiSelectRoles"
                            multiple
                            track-by="val"
                            label="label"
                            value="val"
                            placeholder="กรุณาเลือกตำแหน่ง"
                            selectLabel="เลือก"
                            selectedLabel="เลือกแล้ว"
                            deselect-label="คลิกเพื่อลบ"
                            :options="rolesListTable(data.item)"
                            :searchable="false"
                            :allow-empty="true"
                            :closeOnSelect="false"
                            @input="editRoles(data.item)"
                          ></multiselect>
                        </b-col>

                        <b-col
                          md="2"
                          v-if="data.item.rolesEdited"
                          class="d-flex align-items-center justify-content-md-end"
                        >
                          <b-button
                            size="sm"
                            variant="outline-danger"
                            v-b-tooltip.hover
                            title="ยกเลิกการแก้ไขตำแหน่ง"
                            @click="resetRoles(data.item)"
                          >
                            <i class="fas fa-times-circle"></i>
                          </b-button>
                          <span
                            class="d-inline-block"
                            v-b-tooltip
                            title="กรุณาเลือกอย่างน้อย 1 ตำแหน่ง"
                            :disabled="data.item.multiSelectRoles.length >= 1"
                          >
                            <b-button
                              class="ml-1"
                              size="sm"
                              variant="outline-success"
                              v-b-tooltip.hover
                              title="บันทึกการแก้ไขตำแหน่ง"
                              @click="updateRoles(data.item)"
                              :disabled="data.item.multiSelectRoles.length < 1"
                            >
                              <i class="fas fa-check-circle"></i>
                            </b-button>
                          </span>
                        </b-col>
                      </b-row>
                    </template>

                    <template v-slot:cell(delete)="data">
                      <b-button
                        class="show-when-hovered"
                        size="xs"
                        variant="outline-danger"
                        @click="deleteMember(data.item.user.uid)"
                        v-if="
                          data.item.user &&
                          data.item.user.email != userInfo.email
                        "
                      >
                        <i class="fa fa-trash-alt"></i>
                      </b-button>
                    </template>
                  </b-table>
                </div>
              </b-tab>
              <b-tab title="จัดการแพทย์" @click="ensureBranchSchedule">
                <!-- ภาพรวมตารางเดือน: หมอแต่ละคนลงวันไหน (component เดียวกับ
                     finder ในหน้า QueueManage) -->
                <div class="doctor-overview mb-3">
                  <div class="doctor-overview-head">
                    <strong>ภาพรวมตารางแพทย์</strong>
                    <div class="doctor-overview-nav">
                      <b-button size="sm" variant="light" @click="stepOverviewMonth(-1)">‹</b-button>
                      <span class="doctor-overview-month">{{ overviewMonthLabel }}</span>
                      <b-button size="sm" variant="light" @click="stepOverviewMonth(1)">›</b-button>
                    </div>
                  </div>
                  <DoctorMonthOverview
                    :year="overviewYear"
                    :month="overviewMonth"
                    :members="scheduleMembers"
                    :doctors="overviewDoctors"
                    :highlight-doctor-id="highlightDoctorId"
                    :loading="scheduleLoading"
                    @day-click="onOverviewDayClick"
                    @doctor-click="onOverviewDoctorClick"
                  />
                  <div
                    v-if="highlightDate || highlightDoctorId !== null"
                    class="doctor-overview-banner"
                  >
                    <span v-if="highlightDate">
                      🟢 ลงตรวจ {{ highlightDate }} ({{ highlightedDoctorIds.length }} คน) — แสดงด้านบนของรายชื่อ
                    </span>
                    <span v-else>
                      🔵 {{ highlightDoctorName }} — เน้นวันที่ลงตรวจในปฏิทิน
                    </span>
                    <b-button
                      size="sm"
                      variant="link"
                      class="doctor-overview-clear p-0 ml-2"
                      v-b-tooltip.hover
                      title="ล้างไฮไลต์"
                      @click="clearOverviewHighlight"
                    >
                      <i class="fas fa-eraser"></i>
                    </b-button>
                  </div>
                </div>

                <b-table
                  :style="{ minHeight: '300px' }"
                  outlined
                  noCollapse
                  sort-icon-left
                  :sort-by="highlightDate ? '' : 'creationDt'"
                  :sort-desc="false"
                  head-variant="light"
                  :items="orderedDoctorMembers"
                  :fields="doctorTableField"
                  :tbody-tr-class="doctorRowClass"
                  hover
                  responsive
                  @row-clicked="onDoctorRowClicked"
                >
                  <template #cell(index)="data">{{ data.index + 1 }}.</template>
                  <template #cell(fullName)="data">
                    {{ data.value }}
                    <span
                      v-if="
                        data.item.user && data.item.user.email == userInfo.email
                      "
                      class="bg-secondary rounded p-1 mx-1"
                      >คุณ</span
                    >
                  </template>
                  <template v-slot:cell(doctorCode)="data">
                    <b-input-group v-if="isManager" size="sm">
                      <b-form-input
                        v-model="data.item.doctorCode"
                        trim
                        placeholder="-"
                        :disabled="editingDoctorCodeUid !== data.item.uid"
                        style="max-width: 10ch"
                      ></b-form-input>
                      <b-input-group-append>
                        <template v-if="editingDoctorCodeUid === data.item.uid">
                          <b-button variant="outline-success" @click="confirmUpdateDoctorCode(data.item)">
                            <i class="fas fa-check"></i>
                          </b-button>
                          <b-button variant="outline-danger" @click="cancelEditDoctorCode">
                            <i class="fas fa-times"></i>
                          </b-button>
                        </template>
                        <b-button v-else variant="outline-secondary" @click="startEditDoctorCode(data.item)">
                          <i class="fas fa-edit"></i>
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                    <span v-else>{{ data.item.doctorCode || "-" }}</span>
                  </template>
                  <template v-slot:cell(drApptColor)="data">
                    <VSwatches
                      v-if="isUserDoctor(data.item.authIdItems)"
                      v-model="data.item.drApptColor"
                      :swatches="doctorColorListCode"
                      row-length="5"
                      swatch-size="31"
                      :trigger-style="{ width: '31px', height: '31px' }"
                      @input="updateMember(data.item)"
                    ></VSwatches>
                  </template>
                  <template v-slot:cell(defaultRoomId)="data">
                    <b-form-select
                      v-if="isManager"
                      v-model="data.item.defaultRoomId"
                      :options="getRoomList"
                      value-field="id"
                      text-field="title"
                      size="sm"
                      @change="updateMember(data.item)"
                    >
                      <template #first>
                        <b-form-select-option :value="null"
                          >- ไม่ระบุ -</b-form-select-option
                        >
                      </template>
                    </b-form-select>
                    <span v-else>{{ data.item.defaultRoom?.name || "-" }}</span>
                  </template>
                  <template v-slot:cell(button)="data">
                    <b-button
                      size="sm"
                      :variant="
                        data.item.doctorWorkingHours.length > 0
                          ? 'outline-info'
                          : 'outline-warning'
                      "
                      @click="openWorkingHoursModal(data.item)"
                    >
                      <i class="fas fa-clock"></i>
                    </b-button>
                  </template>
                </b-table>
              </b-tab>
            </b-tabs>
          </b-card>
        </div>
      </div>
    </div>
    <DoctorHourModal ref="DoctorHourModal" @getMember="getMember" />
    <WorkingHoursModal
      ref="workingHoursModal"
      @closed="onWorkingHoursModalClosed"
    />
    <NewUserModal
      ref="newUserModal"
      :branchMembers="branchMembers"
      @getMember="getMember"
    />
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import NewUserModal from "@/components/modal/NewUser";

import Dialog from "@/components/modal/Dialog";
import DoctorHourModal from "@/components/modal/DoctorHour";
import WorkingHoursModal from "@/components/modal/WorkingHoursModal.vue";
import DoctorMonthOverview from "@/components/workingHours/DoctorMonthOverview.vue";
import Loading from "@/components/Loading";
import moment from "moment";
import { doctorIdsOnDate } from "@/utils/doctorMonthOverview";

const TH_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

import VSwatches from "vue-swatches";
// Import the styles too, typically in App.vue or main.js
import "vue-swatches/dist/vue-swatches.css";
import "@/theme/UserManage.scss";
import { eventBus } from "@/main";

export default {
  name: "UserManage",
  components: {
    Dialog,
    Loading,
    VSwatches,
    NewUserModal,
    DoctorHourModal,
    WorkingHoursModal,
    DoctorMonthOverview,
  },
  data() {
    const now = moment();
    return {
      isLoading: false,
      branchMembers: [],
      // month-overview state (จัดการแพทย์ tab)
      overviewYear: now.year(),
      overviewMonth: now.month() + 1,
      scheduleMembers: [],
      scheduleLoading: false,
      scheduleLoadedKey: null, // "YYYY-M" of the last successful load
      highlightDoctorId: null, // clicked doctor row -> highlight their days
      highlightDate: null, // clicked day -> reorder list to that day's doctors
      searchEmail: "",
      searchEmailStatus: false,
      branchMember: null,
      branchAuthority: [],
      branchAuthorityMulti: [],
      drApptColor: "",
      rolesList: [],
      fields: [
        {
          key: "index",
          label: "",
        },
        {
          key: "fullName",
          label: "ชื่อ",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "doctorCode",
          label: "รหัสแพทย์",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "user.email",
          label: "อีเมล",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "authId",
          label: "ตำแหน่ง",
          // class: "tdRoles",
          class: "text-center p-0 align-middle tdRoles narrow-spacing",
        },
        {
          key: "delete",
          label: "",
          class: "text-center  align-middle",
        },
      ],
      doctorTableField: [
        {
          key: "index",
          label: "",
        },
        {
          key: "fullName",
          label: "ชื่อ",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "doctorCode",
          label: "รหัสแพทย์",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "drApptColor",
          label: "สี",
          class: "text-center  align-middle",
        },
        {
          key: "defaultRoomId",
          label: "ห้องตรวจเริ่มต้น",
          class: "text-center  align-middle",
        },
        {
          key: "button",
          label: "วันเวลาทำงาน",
          class: "text-center  align-middle",
        },
      ],
      editingDoctorCodeUid: null,
      state: null,
      invalidFeedback: "",
      validFeedback: "",
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      branchAuthorityList: "moduleMaster/getBranchAuthorityList",
      branchAuthorityBy: "moduleMaster/getBranchAuthorityBy",
      doctorColorList: "moduleMaster/getDoctorColorList",
      userInfo: "moduleUser/getUserInfo",
      isDoctor: "moduleUser/isDoctor",
      isManager: "moduleUser/isManager",
      getRoomList: "moduleUser/getRoomList",
    }),
    doctorColorListCode() {
      return this.doctorColorList.map(item => item.code);
    },
    doctorMembers() {
      return this.branchMembers.filter(i => i.authIdItems?.includes(2));
    },
    // Normalized doctors for DoctorMonthOverview, keyed by branchMemberId (= id).
    overviewDoctors() {
      return this.doctorMembers.map(m => ({
        branchMemberId: m.id,
        name: m.fullName,
        color: m.drApptColor,
      }));
    },
    overviewMonthLabel() {
      return `${TH_MONTHS[this.overviewMonth - 1]} ${this.overviewYear + 543}`;
    },
    // Name of the doctor highlighted via a list-row click (for the banner).
    highlightDoctorName() {
      if (this.highlightDoctorId == null) return "";
      const m = this.doctorMembers.find(d => d.id === this.highlightDoctorId);
      return m ? m.fullName : "";
    },
    // branchMemberIds working the clicked day (for reorder + row highlight).
    highlightedDoctorIds() {
      if (!this.highlightDate) return [];
      return doctorIdsOnDate(this.scheduleMembers, this.highlightDate);
    },
    // When a day is selected, float that day's doctors to the top of the list.
    orderedDoctorMembers() {
      if (!this.highlightDate) return this.doctorMembers;
      const working = new Set(this.highlightedDoctorIds);
      return [...this.doctorMembers].sort(
        (a, b) => (working.has(b.id) ? 1 : 0) - (working.has(a.id) ? 1 : 0)
      );
    },
  },
  watch: {
    searchEmail() {
      this.searchEmailStatus = false;
      this.state = null;
    },
    branchAuthorityMulti() {
      this.branchAuthority = this.branchAuthorityMulti.map(item => item.val);
    },
  },
  methods: {
    ...mapActions({
      fetchBranchMember: "moduleBranch/fetchBranchMember",
      fetchBranchSchedule: "moduleWorkingHours/fetchBranchSchedule",
      updateBranchMember: "moduleBranch/updateBranchMember",
      deleteBranchMember: "moduleBranch/deleteBranchMember",
    }),
    async init() {
      this.getMember();
    },
    async getMember() {
      this.isLoading = true;
      this.editingDoctorCodeUid = null;
      this.fetchBranchMember({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then(res => {
          if (res.status == "200") {
            this.branchMembers = res.data;
            this.branchMembers.forEach(member => {
              member.fullName = member.user.fullName;
              this.$set(member, "multiSelectRoles", []);
              this.$set(member, "rolesEdited", false);
              member.authIdItems.forEach(i => {
                member.multiSelectRoles.push(this.branchAuthorityBy(i, "val"));
              });
            });
          } else {
            this.branchMembers = [];
          }
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
    startEditDoctorCode(member) {
      this.editingDoctorCodeUid = member.uid;
    },
    cancelEditDoctorCode() {
      this.editingDoctorCodeUid = null;
      this.getMember();
    },
    confirmUpdateDoctorCode(branchMember) {
      this.$refs.Dialog.showAlertConfirm(
        "แก้ไขรหัสแพทย์ ?",
        `คุณต้องการแก้ไขรหัสแพทย์ของ "${branchMember.fullName}" เป็น "${branchMember.doctorCode || '-'}"`
      ).then(result => {
        this.editingDoctorCodeUid = null;
        if (result.isConfirmed) {
          this.updateMember(branchMember);
        } else {
          this.getMember();
        }
      });
    },
    updateMember(branchMember) {
      if (branchMember) {
        let data = {
          uid: branchMember.uid,
          authIdItems: branchMember.authIdItems,
          drApptColor: branchMember.drApptColor,
          doctorCode: branchMember.doctorCode,
          defaultRoomId: branchMember.defaultRoomId ?? null,
        };
        this.isLoading = true;
        this.updateBranchMember({
          member: data,
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        })
          .then(res => {
            if (res.status == "200") {
              this.getMember();
              this.setDefaultSearch();
            }
            this.isLoading = false;
          })
          .catch((err) => {
            if (
              err.response?.status === 400 &&
              typeof err.response?.data === "string" &&
              err.response.data.includes("doctor code already exists")
            ) {
              eventBus.$emit("alertToast", {
                message: "รหัสแพทย์นี้มีอยู่ในคลินิกแล้ว",
                variant: "danger",
              });
            }
            this.getMember();
            this.isLoading = false;
          });
      }
    },
    deleteMember(userId) {
      this.$refs.Dialog.showAlertConfirm(
        "ลบผู้ใช้งาน ?",
        "คุณต้องการลบผู้ใช้งาน"
      ).then(result => {
        if (result.value) {
          this.isLoading = true;
          this.deleteBranchMember({
            uid: userId,
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
          })
            .then(res => {
              if (res.status == "200") {
                this.getMember();
              }
              this.isLoading = false;
            })
            .catch(() => {
              this.isLoading = false;
            });
        }
      });
    },
    customLabel(libs) {
      return `${libs.label}`;
    },
    setDefaultSearch() {
      this.searchEmail = "";
      this.searchEmailStatus = false;
      this.branchMember = null;
      this.branchAuthority = [];
      this.branchAuthorityMulti = [];
      this.drApptColor = "";
      this.rolesList = [];
    },
    isUserDoctor(arr) {
      return arr.find(item => item === 2);
    },
    rolesListTable(member) {
      if (member.user?.userTypeId === 1) {
        return this.branchAuthorityList.filter(
          //ตัดหมอออกจาก list
          item => item.val != 2
        );
      } else {
        return this.branchAuthorityList;
      }
    },
    editRoles(member) {
      member.rolesEdited = true;
    },
    resetRoles(member) {
      member.multiSelectRoles = member.authIdItems.map(i =>
        this.branchAuthorityBy(i, "val")
      );
      member.rolesEdited = false;
    },
    updateRoles(member) {
      if (
        this.isMatchedUserLogin(member) &&
        !this.isManagerRoleApplied(member)
      ) {
        this.$refs.Dialog.showAlertInfo(
          "ไม่สามารถลบตำแหน่งผู้จัดการได้",
          "ไม่สามารถลบตำแหน่งผู้จัดการ ออกจากตัวเองได้",
          "error"
        ).then(() => {
          this.resetRoles(member);
        });
      } else {
        let rolesStr = member.multiSelectRoles.map(i => i.label).join(", ");

        this.$refs.Dialog.showAlertConfirm(
          "แก้ไขตำแหน่งผู้ใช้งาน ?",
          `คุณต้องการแก้ไข "${member.user.fullName}" ให้เป็นตำแหน่ง "${rolesStr}"`
        ).then(result => {
          if (result.isConfirmed) {
            member.authIdItems = member.multiSelectRoles
              .map(i => i.val)
              .filter(Boolean); // [,5,4,3,2,1] remove[0]
            this.updateMember(member);
            member.rolesEdited = false;
          } else {
            this.resetRoles(member);
          }
        });
      }
    },
    isMatchedUserLogin(member) {
      return member.uid === this.userInfo.uid;
    },
    isManagerRoleApplied(member) {
      return member.multiSelectRoles.find(i => i.val === 1);
    },
    openNewUserModal() {
      this.$refs.newUserModal.show();
    },
    openWorkingHoursModal({ id }) {
      this.$refs.workingHoursModal.show({ branchMemberId: id });
    },
    // After editing in the modal, the doctor's hours may have changed — refresh
    // the member list and the month-overview so dots/rules stay in sync.
    onWorkingHoursModalClosed() {
      this.getMember();
      if (this.scheduleLoadedKey) this.loadBranchSchedule();
    },
    // ===== month-overview (จัดการแพทย์ tab) =====
    ensureBranchSchedule() {
      const key = `${this.overviewYear}-${this.overviewMonth}`;
      if (this.scheduleLoadedKey !== key) this.loadBranchSchedule();
    },
    async loadBranchSchedule() {
      const key = `${this.overviewYear}-${this.overviewMonth}`;
      const start = moment(
        `${this.overviewYear}-${this.overviewMonth}-01`,
        "YYYY-M-DD"
      );
      this.scheduleLoading = true;
      try {
        const members = await this.fetchBranchSchedule({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          from: start.format("YYYY-MM-DD"),
          to: start.clone().endOf("month").format("YYYY-MM-DD"),
        });
        // Ignore a stale response if the user paged months meanwhile.
        if (`${this.overviewYear}-${this.overviewMonth}` !== key) return;
        this.scheduleMembers = members;
        this.scheduleLoadedKey = key;
      } finally {
        this.scheduleLoading = false;
      }
    },
    stepOverviewMonth(delta) {
      const next = moment(
        `${this.overviewYear}-${this.overviewMonth}-01`,
        "YYYY-M-DD"
      ).add(delta, "month");
      this.overviewYear = next.year();
      this.overviewMonth = next.month() + 1;
      this.highlightDate = null;
      this.loadBranchSchedule();
    },
    onOverviewDayClick(dateStr) {
      this.highlightDate = this.highlightDate === dateStr ? null : dateStr;
      this.highlightDoctorId = null;
    },
    onOverviewDoctorClick(branchMemberId) {
      this.highlightDoctorId =
        this.highlightDoctorId === branchMemberId ? null : branchMemberId;
      this.highlightDate = null;
    },
    onDoctorRowClicked(item) {
      this.highlightDoctorId =
        this.highlightDoctorId === item.id ? null : item.id;
      this.highlightDate = null;
    },
    clearOverviewHighlight() {
      this.highlightDoctorId = null;
      this.highlightDate = null;
    },
    doctorRowClass(item) {
      if (!item) return "";
      if (this.highlightDoctorId !== null && item.id === this.highlightDoctorId)
        return "doctor-row-selected";
      if (this.highlightDate && this.highlightedDoctorIds.includes(item.id))
        return "doctor-row-match";
      return "";
    },
  },
  destroyed() {
    this.$store.dispatch("moduleUser/fetchAllBranchMembers", {
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
    });
  },
};
</script>

<style>
.tdRoles {
  width: 400px;
}
.doctor-overview-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.doctor-overview-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.doctor-overview-month {
  font-weight: 600;
  font-size: 14px;
  min-width: 130px;
  text-align: center;
}
.doctor-overview-banner {
  margin-top: 10px;
  padding: 6px 10px;
  background: #f2fbf6;
  color: #1a8a4a;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.doctor-overview-clear {
  color: #1a8a4a;
  opacity: 0.7;
}
.doctor-overview-clear:hover {
  opacity: 1;
}
.doctor-row-selected > td {
  background-color: #eaf1ff !important;
}
.doctor-row-match > td {
  background-color: #f2fbf6 !important;
}
</style>
